import {
  forwardRef, memo, ReactNode, useCallback, useContext, useImperativeHandle, useMemo,
  useRef, useState
} from 'react'

import './index.scss'
import Input from '../Input'
import TagSelect from '../TagSelect'
import IconSelector from '../IconSelector'
import useDynamicRef from '../../hooks/useDynamicRef'
import Icon from '../Icon'
import { ICON } from '../Icon/data'
import { COLOR } from '../../data/variables'
import UserContext from '../../contexts/UserContext/context'
import FileUpload from '../FileUpload'
import Infobox from '../Infobox'
import { useValueWatcher } from '../../hooks/useValueWatcher'
import { InputError, Input as InputType } from '../Input/types'

type Item = {
  type: 'text' | 'password' | 'textarea' | 'number' | 'select' | 'checkbox' | 'radio' | 'multiselect' | 
    'date' | 'datesimple' | 'daterange' | 'buttonselect' | 'tag' | 'tag-text' | 'icons' | 'file' | 'infobox'
  label: string | ((data: Record<string, any>) => string)
  path: string
  text?: string | ((data: Record<string, any>) => string | null) | ReactNode
  header?: string
  options?: string
  inputLabel?: string
  buttonLabel?: string
  visible?: (data: Record<string, any>) => boolean
  disabled?: (data: Record<string, any>) => boolean
  readonly?: (data: Record<string, any>) => boolean
  children?: Item[] | ((data: Record<string, any>) => Item[])
  arrayKey?: string
  maxFiles?: number
  allowedFiles?: 'all' | 'image'
  format?: string
}

type AddItemForm = {
  inputs: Item[]
  default_data: Record<string, unknown>
  dataInfo?: Record<string, unknown>
  onValueChange?: Record<string, (key: string, value: any) => void>
}

const AddItemForm = memo(forwardRef(({
  inputs, default_data = {}, dataInfo = {}, onValueChange = {}
}: AddItemForm, ref) => {
  const { user } = useContext(UserContext)
  const fileRef = useRef()

  const [data, setData] = useState({ ...default_data, original: { ...default_data } })
  const [error, setError] = useState<Record<string, InputError>>({})
  const [newOptions, setNewOptions] = useState<Record<string, string>>({})

  useValueWatcher(onValueChange, data)

  const onChange = useCallback((key: string, value: any) => {
    setData({ ...data, [key]: value })
  }, [data])

  const options = useMemo(() => {
    return { ...dataInfo, ...newOptions}
  }, [dataInfo, newOptions])

  const getData = useCallback(() => {
    const tempData = { ...data }
    const fileInput = inputs.find((input) => input.type === 'file')
    if (fileInput) {
      tempData[fileInput.path] = fileRef.current.getFiles()
    }
    return tempData
  }, [data])

  const registerErrors = (errors: { [key: string]: InputError }) => {
    setError(errors)
  }

  const forwardRefAction = useDynamicRef({
    getData,
    setError: registerErrors,
    setOptions: setNewOptions
  })
  useImperativeHandle(ref, () => forwardRefAction, [forwardRefAction])

  const getInputField = useCallback((input: Item) => {
    const {
      arrayKey, type, label, path, text, header, options: optionsKey, inputLabel,
      buttonLabel, visible, disabled, readonly, format,
    } = input

    if (!type) return null

    const funcData = { ...data, user_context: user }
    if (typeof visible === 'function' && !visible(funcData)) {
      return
    }

    const params: InputType = {
      type,
      format,
      label: typeof label === 'function' ? label(funcData) : inputLabel ?? label,
      value: data?.[path] ?? undefined,
      onInput: (val?: string | number) => onChange(path, val),
      text: typeof text === 'function' ? text(funcData) : text,
      disabled: typeof disabled === 'function' ? disabled(funcData) : undefined,
      readonly: typeof readonly === 'function' ? readonly(funcData) : undefined,
    }

    if (Array.isArray(params.value) && typeof params.value?.[0] === 'object' && type !== 'file') {
      params.value = params.value.map((val) => val[arrayKey ?? 'id'])
    }

    if (error?.[path]) {
      params.error = error[path]
    }

    if (['select', 'tag'].includes(type)) {
      params.options = options[optionsKey]
      params.searchable = true
    }

    if (['tag-text', 'tag'].includes(type)) {
      return (
        <TagSelect
          {...params}
          type={type === 'tag' ? 'select' : 'text'}
          header={header}
          buttonLabel={(
            <>
              {buttonLabel}
              <Icon icon={ICON.ADD} color={COLOR.WHITE} />
            </>
          )}
        />
      )
    } else if (type === 'icons') {
      if (typeof params.value === 'string') {
        const [icon, color] = params.value.split('_')
        params.value = { icon, color }
      }
      return (
        <IconSelector {...params} />
      )
    } else if (type === 'file') {
      return (
        <FileUpload
          ref={fileRef}
          maxFiles={input.maxFiles ?? 1}
          allowedFiles={input.allowedFiles}
          {...params}
        />
      )
    } else if (type === 'infobox') {
      return (
        <Infobox
          type="inline"
          status="info"
          label={params.label ?? ''}
          message={params.text}
          closable={false}
        />
      )
    }

    return (
      <Input {...params} />
    )
  }, [data, options, error])

  return (
    <div className="AddItemForm">
      {inputs.map((input) => {
        const { children } = input
        if (!children) return getInputField(input)

        const funcData = { ...data, user_context: user }
        const visibleChildren = typeof children === 'function' ? children(funcData) : children ?? []
        return (
          <>
            {getInputField(input)}
            <div className="AddItemForm__children">
              {visibleChildren.map((childInput) => getInputField(childInput))}
            </div>
          </>
        )
      })}
    </div>
  )
}))

export default AddItemForm
